const MONTH_HEADERS = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
];

const DOCUMENT_TYPES = {
  rent_roll: {
    label: 'Rent Roll',
    supportsTenantImport: true,
    recommendedMode: 'direct_import',
    note: 'Looks import-ready for tenant roster, current rent, and balance-based scoring.',
    unlocks: [
      'Current tenant roster and balance import',
      'Action Feed prioritization from snapshot balances',
      'Baseline risk scoring for the current month',
    ],
    nextBestUpload: 'Add resident ledgers or 3 to 6 monthly delinquency exports to improve trend detection.',
  },
  delinquency: {
    label: 'Delinquency Report',
    supportsTenantImport: true,
    recommendedMode: 'map_then_import',
    note: 'Useful for collections triage, balance updates, and urgency ranking. Review mapping before import.',
    unlocks: [
      'Collections prioritization and balance refresh',
      'Past-due segmentation for outreach queues',
      'Stronger triage than a plain rent roll alone',
    ],
    nextBestUpload: 'Pair this with a rent roll and resident ledger history for better payment-behavior forecasting.',
  },
  ledger: {
    label: 'Resident Ledger',
    supportsTenantImport: false,
    recommendedMode: 'history_enrichment',
    note: 'This looks transaction-heavy. Best used to enrich payment history, partial-pay trends, and drift over time.',
    unlocks: [
      'Payment history and days-late trend analysis',
      'Partial-pay and missed-payment pattern detection',
      'Much stronger predictive scoring once linked to tenants',
    ],
    nextBestUpload: 'Import a current rent roll alongside this ledger so tenant records and payment history can be joined.',
  },
  general_ledger: {
    label: 'General Ledger',
    supportsTenantImport: false,
    recommendedMode: 'analytics_review',
    note: 'This looks more like accounting data than resident-level collections data. Use it for portfolio analytics and reconciliation.',
    unlocks: [
      'Property-level financial benchmarking',
      'Revenue and variance analysis',
      'Cross-checking collections outcomes against accounting totals',
    ],
    nextBestUpload: 'Upload a resident-level rent roll or delinquency file for tenant actions, then use this for financial context.',
  },
  t12: {
    label: 'T12 / Financial Summary',
    supportsTenantImport: false,
    recommendedMode: 'analytics_review',
    note: 'This looks like portfolio analytics input, not a tenant import. Keep it for revenue review and operational context.',
    unlocks: [
      'Trailing financial trend review',
      'Month-over-month performance context',
      'Portfolio-level operator reporting',
    ],
    nextBestUpload: 'Upload rent rolls, delinquency reports, or resident ledgers for tenant-level actions and Autopilot.',
  },
  generic: {
    label: 'Spreadsheet Intake',
    supportsTenantImport: true,
    recommendedMode: 'manual_review',
    note: 'Column mapping is available, but this file may need manual review before import.',
    unlocks: [
      'Flexible manual mapping for non-standard exports',
      'Useful fallback when vendor formats vary',
      'Can still seed tenant records if core fields exist',
    ],
    nextBestUpload: 'If possible, export a rent roll, delinquency report, or ledger directly from the source system for cleaner automation.',
  },
};

const FILE_NAME_PATTERNS = {
  rent_roll: ['rent roll', 'rentroll', 'rent_roll', 'unit roster', 'resident roster', 'lease rent'],
  delinquency: ['delinquency', 'deliquency', 'delinquent', 'aged receivable', 'aging', 'collections', 'past due', 'arrears'],
  ledger: ['resident ledger', 'tenant ledger', 'ledger detail', 'transaction detail', 'payment history'],
  general_ledger: ['general ledger', 'gl detail', 'trial balance', 'chart of accounts', 'accounting detail'],
  t12: ['t12', 'trailing 12', '12 month', 'financial summary', 'operating statement'],
};

const HEADER_PATTERNS = {
  rent_roll: ['tenant', 'resident', 'unit', 'rent', 'market rent', 'lease rent', 'phone', 'email'],
  delinquency: ['delinquent', 'past due', 'arrears', 'amount due', 'collections', '30', '60', '90', 'current balance'],
  ledger: ['charge', 'payment', 'credit', 'debit', 'posted', 'transaction', 'receipt', 'balance forward'],
  general_ledger: ['account', 'account number', 'account code', 'debit', 'credit', 'journal', 'gl'],
  t12: [...MONTH_HEADERS, 'actual', 'budget', 'variance', 'noi', 'income', 'expense'],
};

const SOURCE_SYSTEMS = {
  appfolio: {
    label: 'AppFolio',
    patterns: ['appfolio'],
  },
  yardi: {
    label: 'Yardi / Yardi Matrix',
    patterns: ['yardi', 'voyager', 'matrix'],
  },
  entrata: {
    label: 'Entrata',
    patterns: ['entrata'],
  },
  symmetryre: {
    label: 'SymmetryRE',
    patterns: ['symmetryre', 'symmetry re'],
  },
  buildium: {
    label: 'Buildium',
    patterns: ['buildium'],
  },
};

function normalizeList(values = []) {
  return values
    .filter((value) => value !== null && value !== undefined)
    .map((value) => String(value).trim().toLowerCase())
    .filter(Boolean);
}

function normalizeInput(input = []) {
  if (Array.isArray(input)) {
    return {
      fileName: '',
      sheetName: '',
      headers: input,
      rows: [],
    };
  }

  return {
    fileName: String(input.fileName || ''),
    sheetName: String(input.sheetName || ''),
    headers: Array.isArray(input.headers) ? input.headers : [],
    rows: Array.isArray(input.rows) ? input.rows : [],
  };
}

function countMatches(values, patterns) {
  return values.reduce((count, value) => {
    return count + (patterns.some((pattern) => value.includes(pattern)) ? 1 : 0);
  }, 0);
}

function getSampleValues(rows = []) {
  return normalizeList(
    rows.slice(0, 15).flatMap((row) => {
      return Object.values(row || {}).slice(0, 12);
    })
  );
}

function buildSignals(headers = [], sampleValues = []) {
  const hasTenantNames = headers.some((header) =>
    ['tenant', 'resident', 'occupant', 'lessee', 'name'].some((pattern) => header.includes(pattern))
  );
  const hasUnits = headers.some((header) =>
    ['unit', 'apt', 'apartment', 'suite', 'bldg/unit', 'unit number'].some((pattern) => header.includes(pattern))
  );
  const hasRent = headers.some((header) =>
    ['rent', 'lease rent', 'market rent', 'scheduled rent', 'monthly charge'].some((pattern) => header.includes(pattern))
  );
  const hasBalance = headers.some((header) =>
    ['balance', 'amount due', 'amount owed', 'past due', 'arrears', 'delinquent'].some((pattern) => header.includes(pattern))
  );
  const hasContactInfo = headers.some((header) =>
    ['phone', 'mobile', 'cell', 'email', 'contact'].some((pattern) => header.includes(pattern))
  );
  const hasDates = headers.some((header) =>
    ['date', 'posted', 'transaction', 'effective'].some((pattern) => header.includes(pattern))
  ) || sampleValues.some((value) => /\d{1,2}\/\d{1,2}\/\d{2,4}/.test(value));
  const hasTransactions = headers.some((header) =>
    ['charge', 'payment', 'credit', 'debit', 'transaction', 'posted'].some((pattern) => header.includes(pattern))
  );
  const hasAccountingCodes = headers.some((header) =>
    ['account', 'account number', 'account code', 'gl', 'journal'].some((pattern) => header.includes(pattern))
  );
  const monthColumnCount = headers.filter((header) => MONTH_HEADERS.some((month) => header.includes(month))).length;
  const hasMonthlyPeriods = monthColumnCount >= 4;
  const hasBudgetVariance = headers.some((header) =>
    ['budget', 'variance', 'actual', 'noi', 'income', 'expense'].some((pattern) => header.includes(pattern))
  );
  const hasAgingBuckets = headers.some((header) =>
    ['30', '60', '90', '120', 'current'].some((pattern) => header.includes(pattern))
  );

  return {
    hasTenantNames,
    hasUnits,
    hasRent,
    hasBalance,
    hasContactInfo,
    hasDates,
    hasTransactions,
    hasAccountingCodes,
    hasMonthlyPeriods,
    hasBudgetVariance,
    hasAgingBuckets,
  };
}

function scoreType(type, headers, fileTokens, sheetTokens, signals) {
  const basePatterns = HEADER_PATTERNS[type] || [];
  const headerScore = countMatches(headers, basePatterns);
  const fileScore = countMatches(fileTokens, FILE_NAME_PATTERNS[type] || []) * 3;
  const sheetScore = countMatches(sheetTokens, FILE_NAME_PATTERNS[type] || []) * 2;

  let signalScore = 0;
  if (type === 'rent_roll' && signals.hasTenantNames && signals.hasUnits && (signals.hasRent || signals.hasBalance)) {
    signalScore += 4;
  }
  if (type === 'delinquency' && signals.hasBalance) {
    signalScore += 2;
  }
  if (type === 'delinquency' && signals.hasAgingBuckets) {
    signalScore += 4;
  }
  if (type === 'ledger' && signals.hasTransactions && signals.hasDates) {
    signalScore += 4;
  }
  if (type === 'general_ledger' && signals.hasAccountingCodes && signals.hasTransactions) {
    signalScore += 4;
  }
  if (type === 't12' && signals.hasMonthlyPeriods && signals.hasBudgetVariance) {
    signalScore += 5;
  }

  return headerScore + fileScore + sheetScore + signalScore;
}

function getDataCoverage(type, signals) {
  if (type === 't12' || type === 'general_ledger') {
    return {
      level: 'portfolio_context',
      label: 'Portfolio context only',
      guidance: 'Useful for financial context, but not enough for tenant-level outreach or scoring on its own.',
    };
  }

  if (type === 'ledger' && signals.hasDates && signals.hasTransactions) {
    return {
      level: 'history_ready',
      label: 'Payment-history ready',
      guidance: 'Great for late-trend and partial-pay analysis once linked to a current tenant roster.',
    };
  }

  if (signals.hasTenantNames && signals.hasUnits && (signals.hasRent || signals.hasBalance) && signals.hasDates && signals.hasTransactions) {
    return {
      level: 'predictive_ready',
      label: 'Predictive-ready',
      guidance: 'This has the ingredients for stronger scoring, trend analysis, and better Autopilot prioritization.',
    };
  }

  if (signals.hasTenantNames && signals.hasUnits && (signals.hasRent || signals.hasBalance)) {
    return {
      level: 'snapshot_ready',
      label: 'Snapshot-ready',
      guidance: 'Enough to start tenant scoring and an Action Feed today, even before a deeper integration.',
    };
  }

  return {
    level: 'review_needed',
    label: 'Needs review',
    guidance: 'The file may still be useful, but Tenafi will need manual mapping or companion uploads to use it well.',
  };
}

function getConfidence(topScore, secondScore) {
  if (topScore >= 9 && topScore - secondScore >= 3) {
    return 'high';
  }
  if (topScore >= 4) {
    return 'medium';
  }
  return 'low';
}

function buildDetectedSignals(type, signals) {
  const detected = [];

  if (signals.hasTenantNames && signals.hasUnits) {
    detected.push('Tenant and unit identity fields found');
  }
  if (signals.hasRent || signals.hasBalance) {
    detected.push('Current rent or balance fields found');
  }
  if (signals.hasAgingBuckets) {
    detected.push('Collections aging buckets found');
  }
  if (signals.hasDates && signals.hasTransactions) {
    detected.push('Transaction history columns found');
  }
  if (signals.hasContactInfo) {
    detected.push('Contact fields found for SMS and voice outreach');
  }
  if (signals.hasMonthlyPeriods) {
    detected.push('Monthly financial periods found');
  }
  if (signals.hasAccountingCodes && type === 'general_ledger') {
    detected.push('Accounting code structure found');
  }

  return detected;
}

function detectSourceSystem(fileTokens, headers) {
  const values = [...fileTokens, ...headers];
  const scored = Object.entries(SOURCE_SYSTEMS)
    .map(([sourceSystem, config]) => ({
      sourceSystem,
      score: countMatches(values, config.patterns),
    }))
    .sort((left, right) => right.score - left.score);

  const top = scored[0];
  if (!top || top.score === 0) {
    return {
      sourceSystem: 'unknown',
      sourceSystemLabel: 'Unknown / custom export',
    };
  }

  return {
    sourceSystem: top.sourceSystem,
    sourceSystemLabel: SOURCE_SYSTEMS[top.sourceSystem].label,
  };
}

function detectDocumentProfile(input = []) {
  const { fileName, sheetName, headers: rawHeaders, rows } = normalizeInput(input);
  const headers = normalizeList(rawHeaders);
  const fileTokens = normalizeList([fileName]);
  const sheetTokens = normalizeList([sheetName]);
  const sampleValues = getSampleValues(rows);
  const signals = buildSignals(headers, sampleValues);
  const sourceSystem = detectSourceSystem(fileTokens, headers);

  const scored = Object.keys(DOCUMENT_TYPES)
    .filter((type) => type !== 'generic')
    .map((type) => ({
      type,
      score: scoreType(type, headers, fileTokens, sheetTokens, signals),
    }))
    .sort((left, right) => right.score - left.score);

  const top = scored[0] || { type: 'generic', score: 0 };
  const second = scored[1] || { score: 0 };
  const type = top.score > 1 ? top.type : 'generic';
  const dataCoverage = getDataCoverage(type, signals);

  return {
    type,
    confidence: getConfidence(top.score, second.score),
    dataCoverage,
    signals: buildDetectedSignals(type, signals),
    sourceSystem,
  };
}

export function detectDocumentType(input = []) {
  return detectDocumentProfile(input).type;
}

export function getDocumentTypeLabel(type) {
  return DOCUMENT_TYPES[type]?.label || DOCUMENT_TYPES.generic.label;
}

export function getDocumentIntelligenceSummary(input = []) {
  const profile = detectDocumentProfile(input);
  const typeConfig = DOCUMENT_TYPES[profile.type] || DOCUMENT_TYPES.generic;

  return {
    type: profile.type,
    label: typeConfig.label,
    confidence: profile.confidence,
    supportsTenantImport: typeConfig.supportsTenantImport,
    recommendedMode: typeConfig.recommendedMode,
    note: typeConfig.note,
    unlocks: typeConfig.unlocks,
    nextBestUpload: typeConfig.nextBestUpload,
    signals: profile.signals,
    dataCoverage: profile.dataCoverage.level,
    dataCoverageLabel: profile.dataCoverage.label,
    dataCoverageGuidance: profile.dataCoverage.guidance,
    sourceSystem: profile.sourceSystem.sourceSystem,
    sourceSystemLabel: profile.sourceSystem.sourceSystemLabel,
  };
}
